import React, { useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Download, Printer, Search, Upload } from 'lucide-react';
import { useDatos } from '../contexts/DatosContext';
import type { Clase, Evaluacion } from '../types';
import { ESTADOS, NIVELES_CLASE, OBJETIVOS, POBLACIONES } from '../data/constantes';
import { Boton } from '../components/ui/Boton';
import { Tarjeta } from '../components/ui/Tarjeta';
import { Modal } from '../components/ui/Modal';
import { Aviso } from '../components/ui/Aviso';
import { Insignia } from '../components/ui/Etiquetas';
import { CampoTexto } from '../components/ui/Campo';
import { EvaluacionModal } from '../components/clases/EvaluacionModal';
import { duplicarClase, formatearFecha, formatearFechaHora } from '../utils/clase';
const TODOS = 'Todos';
const TODAS = 'Todas';
export function MisClases() {
  const {
    clases,
    guardarClase,
    eliminarClase,
    exportarRespaldo,
    importarRespaldo,
    notificar
  } = useDatos();
  const navegar = useNavigate();
  const entradaArchivo = useRef<HTMLInputElement>(null);
  const [busqueda, setBusqueda] = useState('');
  const [fecha, setFecha] = useState('');
  const [nivel, setNivel] = useState(TODOS);
  const [poblacion, setPoblacion] = useState(TODAS);
  const [objetivo, setObjetivo] = useState(TODOS);
  const [estado, setEstado] = useState(TODOS);
  const [orden, setOrden] = useState('Modificación');
  const [aEliminar, setAEliminar] = useState<Clase | null>(null);
  const [aEvaluar, setAEvaluar] = useState<Clase | null>(null);
  const [detalle, setDetalle] = useState<Clase | null>(null);
  const [importando, setImportando] = useState<{
    texto: string;
    nombre: string;
  } | null>(null);
  const [errorImportacion, setErrorImportacion] = useState('');
  const [confirmacionReemplazo, setConfirmacionReemplazo] = useState('');
  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    const lista = clases.filter(c => {
      if (q) {
        const texto = `${c.titulo} ${c.grupo} ${c.objetivo} ${c.objetivoPersonalizado}`.toLowerCase();
        if (!texto.includes(q)) return false;
      }
      if (fecha && c.fecha !== fecha) return false;
      if (nivel !== TODOS && c.nivel !== nivel) return false;
      if (poblacion !== TODAS && c.poblacion !== poblacion) return false;
      if (objetivo !== TODOS && c.objetivo !== objetivo) return false;
      if (estado !== TODOS && c.estado !== estado) return false;
      return true;
    });
    return [...lista].sort((a, b) => {
      if (orden === 'Alfabético') return a.titulo.localeCompare(b.titulo, 'es');
      if (orden === 'Fecha de clase') return (b.fecha || '').localeCompare(a.fecha || '');
      return new Date(b.modificadoEn).getTime() - new Date(a.modificadoEn).getTime();
    });
  }, [clases, busqueda, fecha, nivel, poblacion, objetivo, estado, orden]);
  const alImportarArchivo = async (archivo: File) => {
    try {
      const texto = await archivo.text();
      setErrorImportacion('');
      setImportando({
        texto,
        nombre: archivo.name
      });
    } catch {
      setErrorImportacion('No se pudo leer el archivo seleccionado.');
    }
  };
  const ejecutarImportacion = (modo: 'combinar' | 'reemplazar') => {
    if (!importando) return;
    const resultado = importarRespaldo(importando.texto, modo);
    if (!resultado.ok) {
      setErrorImportacion(resultado.mensaje);
      return;
    }
    notificar(resultado.mensaje);
    setImportando(null);
    setConfirmacionReemplazo('');
    setErrorImportacion('');
  };
  const guardarEvaluacion = (evaluacion: Evaluacion) => {
    if (!aEvaluar) return;
    guardarClase({
      ...aEvaluar,
      estado: 'Realizada',
      evaluacion
    });
    notificar('Evaluación guardada.');
    setAEvaluar(null);
  };
  const crearProxima = (evaluacion: Evaluacion) => {
    if (!aEvaluar) return;
    guardarClase({
      ...aEvaluar,
      estado: 'Realizada',
      evaluacion
    });
    const proxima = duplicarClase({
      ...aEvaluar,
      evaluacion
    }, {
      paraProxima: true
    });
    const guardada = guardarClase(proxima);
    setAEvaluar(null);
    notificar('Se creó la próxima clase a partir de esta.');
    navegar(`/nueva/${guardada.id}`);
  };
  return <div className="space-y-5" data-id="element-675" data-mp="1ipse3ql6cyf">
      <div className="flex flex-wrap items-end justify-between gap-3" data-id="element-676" data-mp="1jshc64cm5bp">
        <div data-id="element-677" data-mp="3so925191825a">
          <h1 className="text-2xl font-extrabold text-tinta-titulo sm:text-3xl" data-id="element-678" data-mp="3snzdpi3my31p">Mis clases</h1>
          <p className="mt-1 text-sm text-tinta-sec" data-id="element-679" data-mp="3omx9snyrx2te">
            {clases.length} {clases.length === 1 ? 'clase guardada' : 'clases guardadas'} en este
            dispositivo.
          </p>
        </div>
        <div className="flex flex-wrap gap-2" data-id="element-680" data-mp="3ewaz985wz2hk">
          <Boton variante="contorno" onClick={exportarRespaldo} data-id="element-681" data-mp="39ysqbhrwcn9k">
            <Download className="h-4 w-4" aria-hidden="true" data-id="element-682" data-mp="39z2ecztdskcm" />
            Exportar respaldo
          </Boton>
          <Boton variante="contorno" onClick={() => entradaArchivo.current?.click()} data-id="element-683" data-mp="34xmw1fil8rxx">
            <Upload className="h-4 w-4" aria-hidden="true" data-id="element-684" data-mp="30xb9fx6o0hjt" />
            Importar respaldo
          </Boton>
          <input ref={entradaArchivo} type="file" accept="application/json,.json" className="sr-only" onChange={e => {
          const archivo = e.target.files?.[0];
          if (archivo) alImportarArchivo(archivo);
          e.target.value = '';
        }} data-id="element-685" data-mp="2w14d8w42mkf2" />
        </div>
      </div>

      {errorImportacion && <Aviso tipo="alerta" titulo="Error de importación" data-id="element-686" data-mp="1pensgslq41c8">
          {errorImportacion}
        </Aviso>}

      <Tarjeta data-id="element-687" data-mp="ws2rr1jcycw0">
        <label className="relative block" data-id="element-688" data-mp="wrtac2cdpe1x">
          <span className="sr-only" data-id="element-689" data-mp="wsm9oi90d1a1">Buscar por título, grupo u objetivo</span>
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-tinta-tenue" aria-hidden="true" data-id="element-690" data-mp="11q4mijq5c0nb" />
          <input type="search" value={busqueda} onChange={e => setBusqueda(e.target.value)} placeholder="Buscar por título, grupo u objetivo" className="min-h-[44px] w-full rounded-xl border border-linea bg-white pl-9 pr-3 text-sm focus:border-vino focus:outline-none focus:ring-2 focus:ring-vino/20" data-id="element-691" data-mp="92rxm2wijgqu" />
        </label>

        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-6" data-id="element-692" data-mp="1q4szpknt1v88">
          <Filtro etiqueta="Fecha" data-id="element-693" data-mp="1q6ndmf0nsn2t">
            <input type="date" value={fecha} onChange={e => setFecha(e.target.value)} className="min-h-[44px] w-full rounded-xl border border-linea bg-white px-3 text-sm focus:border-vino focus:outline-none focus:ring-2 focus:ring-vino/20" data-id="element-694" data-mp="1m2z3ppok1fee" />
          </Filtro>
          <SelectorFiltro etiqueta="Nivel" valor={nivel} onChange={setNivel} opciones={[TODOS, ...NIVELES_CLASE]} data-id="element-695" data-mp="17fw85ojslgtp" />
          <SelectorFiltro etiqueta="Población" valor={poblacion} onChange={setPoblacion} opciones={[TODAS, ...POBLACIONES]} data-id="element-696" data-mp="12dx56fryslxa" />
          <SelectorFiltro etiqueta="Objetivo" valor={objetivo} onChange={setObjetivo} opciones={[TODOS, ...OBJETIVOS]} data-id="element-697" data-mp="2dtvfso2sjg0m" />
          <SelectorFiltro etiqueta="Estado" valor={estado} onChange={setEstado} opciones={[TODOS, ...ESTADOS]} data-id="element-698" data-mp="2mdic5vvanf7o" />
          <SelectorFiltro etiqueta="Ordenar por" valor={orden} onChange={setOrden} opciones={['Modificación', 'Fecha de clase', 'Alfabético']} data-id="element-699" data-mp="2cd7wm4lsdvpc" />
        </div>
      </Tarjeta>

      {clases.length === 0 ? <Tarjeta data-id="element-700" data-mp="352f5nxiva3ou">
          <p className="text-sm text-tinta-sec" data-id="element-701" data-mp="353zz1jtg3gl4">
            Todavía no guardaste ninguna clase. Creá la primera y usala como base para las próximas.
          </p>
          <Boton className="mt-3" onClick={() => navegar('/nueva')} data-id="element-702" data-mp="2vbarh010mmv2">
            Crear nueva clase
          </Boton>
        </Tarjeta> : filtradas.length === 0 ? <Tarjeta data-id="element-703" data-mp="3o09cq60fq54l">
          <p className="text-sm font-semibold text-tinta-titulo" data-id="element-704" data-mp="3nzq4jh0l6v3r">Sin resultados</p>
          <p className="mt-1 text-sm text-tinta-sec" data-id="element-705" data-mp="3srzsiri55l3e">
            Probá con otra búsqueda o quitá algunos filtros.
          </p>
        </Tarjeta> : <ul className="grid grid-cols-1 gap-3 lg:grid-cols-2" data-id="element-706" data-mp="1srbbip1vgtg7">
          {filtradas.map(c => <li key={c.id} data-id="element-707" data-mp="1nwf70pfg8dm7">
              <Tarjeta className="h-full" data-id="element-708" data-mp="1iz5idbzvw5zf">
                <div className="flex flex-wrap items-start justify-between gap-2" data-id="element-709" data-mp="1izyhprwijt7j">
                  <div data-id="element-710" data-mp="1eyv2n7mai17d">
                    <h2 className="text-base font-bold text-tinta-titulo" data-id="element-711" data-mp="1ezoe2lw71evu">
                      {c.titulo || 'Sin título'}
                    </h2>
                    <p className="mt-1 text-xs text-tinta-tenue" data-id="element-712" data-mp="26ohrycz4ig29">
                      Modificada el {formatearFechaHora(c.modificadoEn)}
                    </p>
                  </div>
                  <Insignia tono={c.estado === 'Realizada' ? 'verde' : c.estado === 'Borrador' ? 'oro' : 'vino'} data-id="element-713" data-mp="1xol8qqvi69el">
                    {c.estado}
                  </Insignia>
                </div>

                <div className="mt-3 flex flex-wrap gap-1.5" data-id="element-714" data-mp="m84madlj6si8">
                  <Insignia data-id="element-715" data-mp="2xwrobouy4eg1">{formatearFecha(c.fecha)}</Insignia>
                  {c.grupo && <Insignia data-id="element-716" data-mp="2xuezcrqrt3th">{c.grupo}</Insignia>}
                  <Insignia tono="vino" data-id="element-717" data-mp="32ut9cfbxmnu5">{c.nivel}</Insignia>
                  <Insignia data-id="element-718" data-mp="32t8esppmb4wa">{c.duracion} min</Insignia>
                  <Insignia tono="azul" data-id="element-719" data-mp="36wegdk1sc7wf">
                    {c.objetivo === 'Personalizado' ? c.objetivoPersonalizado || 'Personalizado' : c.objetivo}
                  </Insignia>
                </div>

                <div className="mt-4 flex flex-wrap gap-2" data-id="element-720" data-mp="3gm7t4odnyt29">
                  <Boton tamano="sm" variante="contorno" onClick={() => setDetalle(c)} data-id="element-721" data-mp="3llko5weov6fx">
                    Abrir
                  </Boton>
                  <Boton tamano="sm" variante="contorno" onClick={() => navegar(`/nueva/${c.id}`)} data-id="element-722" data-mp="3qlouitlvuksf">
                    Editar
                  </Boton>
                  <Boton tamano="sm" variante="texto" onClick={() => {
              const copia = duplicarClase(c);
              guardarClase(copia);
              notificar('Clase duplicada con un identificador nuevo.');
            }} data-id="element-723" data-mp="1w7fjyk5y4yiq">
                    Duplicar
                  </Boton>
                  <Boton tamano="sm" variante="texto" onClick={() => setAEvaluar(c)} data-id="element-724" data-mp="1b6vp5uylz0as">
                    {c.estado === 'Realizada' ? 'Ver evaluación' : 'Marcar como realizada'}
                  </Boton>
                  <Boton tamano="sm" variante="texto" onClick={() => navegar(`/imprimir/${c.id}`)} data-id="element-725" data-mp="1l5bg9xxmy9ka">
                    <Printer className="h-4 w-4" aria-hidden="true" data-id="element-726" data-mp="1p5v8qo79kp03" />
                    Imprimir
                  </Boton>
                  <Boton tamano="sm" variante="peligro" onClick={() => setAEliminar(c)} data-id="element-727" data-mp="1tyenvpllns70">
                    Eliminar
                  </Boton>
                </div>
              </Tarjeta>
            </li>)}
        </ul>}

      <Modal abierto={Boolean(aEliminar)} titulo="Eliminar clase" onCerrar={() => setAEliminar(null)} ancho="sm" acciones={<>
            <Boton variante="contorno" onClick={() => setAEliminar(null)} data-id="element-729" data-mp="3tdq0pr8ujn9t">
              Cancelar
            </Boton>
            <Boton variante="peligro" onClick={() => {
        if (!aEliminar) return;
        eliminarClase(aEliminar.id);
        notificar('Clase eliminada.');
        setAEliminar(null);
      }} data-id="element-730" data-mp="3ofoi17fc1pyv">
              Eliminar definitivamente
            </Boton>
          </>} data-id="element-728" data-mp="23t6ol2ahav60">
        <p className="text-sm text-tinta" data-id="element-731" data-mp="azqhwtosr3nd">
          Vas a eliminar “{aEliminar?.titulo || 'Sin título'}”. Esta acción no se puede deshacer.
        </p>
      </Modal>

      <Modal abierto={Boolean(detalle)} titulo={detalle?.titulo || 'Clase'} descripcion={detalle ? `${formatearFecha(detalle.fecha)} · ${detalle.nivel} · ${detalle.duracion} min · ${detalle.estado}` : undefined} onCerrar={() => setDetalle(null)} acciones={<>
            <Boton variante="contorno" onClick={() => detalle && navegar(`/nueva/${detalle.id}`)} data-id="element-733" data-mp="2o789cczjsxni">
              Editar
            </Boton>
            <Boton onClick={() => detalle && navegar(`/imprimir/${detalle.id}`)} data-id="element-734" data-mp="2efu6pwcavhq5">
              Imprimir
            </Boton>
          </>} data-id="element-732" data-mp="195orlrw6rbu">
        {detalle && <div className="space-y-3" data-id="element-735" data-mp="25enkd6myzcoz">
            <p className="text-sm text-tinta-sec" data-id="element-736" data-mp="25dkun21dvgan">
              Grupo: {detalle.grupo || '—'} · Población: {detalle.poblacion} · Participantes:{' '}
              {detalle.participantes} · Puntos: {detalle.puntos}
            </p>
            <ol className="space-y-2" data-id="element-737" data-mp="1viaqo8rfcyu8">
              {detalle.bloques.map(b => <li key={b.uid} className="rounded-xl border border-linea p-3" data-id="element-738" data-mp="20g2v2299adfb">
                  <div className="flex items-center justify-between gap-2" data-id="element-739" data-mp="3pvitonpk0gqy">
                    <h3 className="text-sm font-bold text-tinta-titulo" data-id="element-740" data-mp="3uu3uao8px2fx">{b.nombre}</h3>
                    <Insignia tono="vino" data-id="element-741" data-mp="1j5yz13jldd4a">{b.minutos} min</Insignia>
                  </div>
                  {b.ejercicios.length > 0 && <ul className="mt-2 space-y-1 text-sm text-tinta-sec" data-id="element-742" data-mp="1e8gml36d8frj">
                      {b.ejercicios.map(e => <li key={e.uid} data-id="element-743" data-mp="1sy4pg8u73mhd">
                          {e.nombre} · {e.minutos} min · {e.variacion} · Lado: {e.lado}
                        </li>)}
                    </ul>}
                </li>)}
            </ol>
            {detalle.evaluacion && <div className="rounded-xl border border-verde/30 bg-verde/5 p-3 text-sm" data-id="element-744" data-mp="1wzzmh153fdpc">
                <h3 className="font-bold text-tinta-titulo" data-id="element-745" data-mp="2bste1kxn3kad">Evaluación</h3>
                <p className="mt-1 text-tinta-sec" data-id="element-746" data-mp="26wcfrxwq7pjk">Resultado: {detalle.evaluacion.resultado}</p>
                {detalle.evaluacion.proximoObjetivo && <p className="mt-1 text-tinta-sec" data-id="element-747" data-mp="cibjnksk9gn6">
                    Próximo objetivo: {detalle.evaluacion.proximoObjetivo}
                  </p>}
              </div>}
          </div>}
      </Modal>

      <EvaluacionModal clase={aEvaluar} abierto={Boolean(aEvaluar)} onCerrar={() => setAEvaluar(null)} onGuardar={guardarEvaluacion} onProximaClase={crearProxima} data-id="element-748" data-mp="34o14fj6uexu5" />

      <Modal abierto={Boolean(importando)} titulo="Importar respaldo" descripcion={importando?.nombre} onCerrar={() => {
      setImportando(null);
      setConfirmacionReemplazo('');
    }} ancho="sm" acciones={<>
            <Boton variante="contorno" onClick={() => {
        setImportando(null);
        setConfirmacionReemplazo('');
      }} data-id="element-750" data-mp="2pyv4tu43f5lq">
              Cancelar
            </Boton>
            <Boton onClick={() => ejecutarImportacion('combinar')} data-id="element-751" data-mp="22au1r0poohdp">Combinar sin duplicar</Boton>
            <Boton variante="peligro" disabled={confirmacionReemplazo.trim().toUpperCase() !== 'REEMPLAZAR'} onClick={() => ejecutarImportacion('reemplazar')} data-id="element-752" data-mp="228hcs3lid6r5">
              Reemplazar todos los datos
            </Boton>
          </>} data-id="element-749" data-mp="3dp7k5q1o44mc">
        <div className="space-y-3" data-id="element-753" data-mp="1grjp5ldfwz60">
          <p className="text-sm text-tinta" data-id="element-754" data-mp="1gqi36luvys9i">
            “Combinar sin duplicar” suma las clases y ejercicios que todavía no existen en este
            dispositivo.
          </p>
          <Aviso tipo="alerta" titulo="Reemplazar borra los datos actuales" data-id="element-755" data-mp="1718bh73yfblx">
            Para habilitar el reemplazo escribí REEMPLAZAR en el campo.
          </Aviso>
          <CampoTexto etiqueta="Confirmación de reemplazo" value={confirmacionReemplazo} placeholder="REEMPLAZAR" onChange={e => setConfirmacionReemplazo(e.target.value)} data-id="element-756" data-mp="120au2wqrqllh" />
        </div>
      </Modal>
    </div>;
}
function Filtro({
  etiqueta,
  children
}: {
  etiqueta: string;
  children: React.ReactNode;
}) {
  return <label className="block" data-id="element-757" data-mp="edp54r2az5iz">
      <span className="mb-1 block text-xs font-semibold text-tinta-titulo" data-id="element-758" data-mp="jed2rvl1ekm6">{etiqueta}</span>
      {children}
    </label>;
}
function SelectorFiltro({
  etiqueta,
  valor,
  onChange,
  opciones
}: {
  etiqueta: string;
  valor: string;
  onChange: (v: string) => void;
  opciones: string[];
}) {
  return <Filtro etiqueta={etiqueta} data-id="element-759" data-mp="x5rpodi2f5yi">
      <select value={valor} onChange={e => onChange(e.target.value)} className="min-h-[44px] w-full rounded-xl border border-linea bg-white px-3 text-sm focus:border-vino focus:outline-none focus:ring-2 focus:ring-vino/20" data-id="element-760" data-mp="x6sy0a7p5ecj">
        {opciones.map(o => <option key={o} value={o} data-id="element-761" data-mp="se9k1c53kh77">
            {o}
          </option>)}
      </select>
    </Filtro>;
}